import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Page,
  Layout,
  Card,
  Toast,
  PageActions,
  Box,
  Spinner,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Banner,
  Divider,
  Button,
  ButtonGroup,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { SessionScoringForm } from '../components/SessionScoringForm';
import { PopupSettingsForm } from '../components/PopupSettingsForm';
import { useAuthenticatedFetch } from '../hooks/useApi';
import {
  SessionScoringSettings,
  MerchantPopupSettings,
  defaultSessionScoring,
  defaultPopupSettings,
} from '../types';

type StrategyPreset = 'conservative' | 'balanced' | 'aggressive' | 'custom';

const PRESETS: Record<Exclude<StrategyPreset, 'custom'>, { scoring: Partial<SessionScoringSettings>; popup: Partial<MerchantPopupSettings> }> = {
  conservative: {
    scoring: {
      enabled: true,
      thresholds: { low: 35, medium: 60, high: 85 },
    },
    popup: {
      enabled: true,
      exitIntentEnabled: false,
      highIntentUpsellEnabled: false,
      coldAggressiveness: 10,
      warmAggressiveness: 30,
      hotAggressiveness: 50,
      dynamicProductCount: true,
      coldProductCount: 2,
      warmProductCount: 3,
      hotProductCount: 4,
      intentShippingBar: false,
    },
  },
  balanced: {
    scoring: {
      enabled: true,
      thresholds: { low: 25, medium: 50, high: 75 },
    },
    popup: {
      enabled: true,
      exitIntentEnabled: true,
      highIntentUpsellEnabled: true,
      highIntentMinScore: 75,
      coldAggressiveness: 30,
      warmAggressiveness: 60,
      hotAggressiveness: 90,
      dynamicProductCount: true,
      coldProductCount: 3,
      warmProductCount: 4,
      hotProductCount: 6,
      intentShippingBar: true,
    },
  },
  aggressive: {
    scoring: {
      enabled: true,
      thresholds: { low: 15, medium: 35, high: 55 },
    },
    popup: {
      enabled: true,
      exitIntentEnabled: true,
      highIntentUpsellEnabled: true,
      highIntentMinScore: 55,
      coldAggressiveness: 60,
      warmAggressiveness: 85,
      hotAggressiveness: 100,
      dynamicProductCount: true,
      coldProductCount: 4,
      warmProductCount: 6,
      hotProductCount: 8,
      intentShippingBar: true,
    },
  },
};

function detectPreset(scoring: SessionScoringSettings, popup: MerchantPopupSettings): StrategyPreset {
  for (const [name, preset] of Object.entries(PRESETS) as [Exclude<StrategyPreset, 'custom'>, typeof PRESETS['balanced']][]) {
    if (
      scoring.thresholds.low === preset.scoring.thresholds?.low &&
      scoring.thresholds.medium === preset.scoring.thresholds?.medium &&
      scoring.thresholds.high === preset.scoring.thresholds?.high &&
      popup.coldAggressiveness === preset.popup.coldAggressiveness &&
      popup.warmAggressiveness === preset.popup.warmAggressiveness &&
      popup.hotAggressiveness === preset.popup.hotAggressiveness
    ) {
      return name;
    }
  }
  return 'custom';
}

export default function SalesStrategyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shop, settings, refreshSettings, isLoading } = useShop();
  const fetchWithAuth = useAuthenticatedFetch();

  const [sessionScoring, setSessionScoring] = useState<SessionScoringSettings>(
    settings?.sessionScoring || defaultSessionScoring
  );
  const [popupSettings, setPopupSettings] = useState<MerchantPopupSettings>(
    settings?.popupSettings || defaultPopupSettings
  );
  const [activePreset, setActivePreset] = useState<StrategyPreset>('custom');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Sync from context when settings load
  useEffect(() => {
    if (settings) {
      const sc = settings.sessionScoring || defaultSessionScoring;
      const pp = settings.popupSettings || defaultPopupSettings;
      setSessionScoring(sc);
      setPopupSettings(pp);
      setActivePreset(detectPreset(sc, pp));
    }
  }, [settings]);

  const handleScoringChange = useCallback((s: SessionScoringSettings) => {
    setSessionScoring(s);
    setHasChanges(true);
    setActivePreset(detectPreset(s, popupSettings));
  }, [popupSettings]);

  const handlePopupChange = useCallback((p: MerchantPopupSettings) => {
    setPopupSettings(p);
    setHasChanges(true);
    setActivePreset(detectPreset(sessionScoring, p));
  }, [sessionScoring]);

  const applyPreset = useCallback((preset: Exclude<StrategyPreset, 'custom'>) => {
    const p = PRESETS[preset];
    const newScoring: SessionScoringSettings = {
      ...sessionScoring,
      ...p.scoring,
      thresholds: { ...sessionScoring.thresholds, ...p.scoring.thresholds },
    };
    const newPopup: MerchantPopupSettings = {
      ...popupSettings,
      ...p.popup,
    };
    setSessionScoring(newScoring);
    setPopupSettings(newPopup);
    setActivePreset(preset);
    setHasChanges(true);
  }, [sessionScoring, popupSettings]);

  const handleSave = async () => {
    if (!shop) return;
    setIsSaving(true);
    try {
      await fetchWithAuth('/api/shopify/settings', {
        method: 'POST',
        body: {
          sessionScoring,
          popupSettings,
        },
      });
      setToastMessage(t('settings.saved', 'Settings saved successfully!'));
      setHasChanges(false);
      await refreshSettings();
    } catch (error) {
      setToastMessage(t('common.error', 'Failed to save settings.'));
      console.error('Error saving strategy:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setSessionScoring(settings?.sessionScoring || defaultSessionScoring);
    setPopupSettings(settings?.popupSettings || defaultPopupSettings);
    setHasChanges(false);
    if (settings) {
      setActivePreset(detectPreset(
        settings.sessionScoring || defaultSessionScoring,
        settings.popupSettings || defaultPopupSettings,
      ));
    }
  };

  if (isLoading) {
    return (
      <Page title={t('strategy.title', 'Sales Strategy')}>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800" minHeight="300px">
                <BlockStack gap="400" inlineAlign="center">
                  <Spinner size="large" />
                  <Text as="p" alignment="center">{t('common.loading', 'Loading...')}</Text>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t('strategy.title', 'Sales Strategy')}
      subtitle={t('strategy.subtitle', 'Configure how your store engages visitors and drives conversions')}
      backAction={{ onAction: () => navigate('/app') }}
    >
      <Layout>
        {/* Strategy Overview Banner */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text variant="headingLg" as="h2">
                    {t('strategy.quickSetup', 'Quick Setup')}
                  </Text>
                  <Text as="p" tone="subdued">
                    {t('strategy.quickSetupDesc', 'Choose a preset that matches your store\'s audience, or customize every detail below.')}
                  </Text>
                </BlockStack>
                <Badge tone={sessionScoring.enabled && popupSettings.enabled ? 'success' : 'attention'} size="large">
                  {sessionScoring.enabled && popupSettings.enabled
                    ? t('strategy.fullyActive', 'Fully Active')
                    : t('strategy.partiallyActive', 'Partially Active')}
                </Badge>
              </InlineStack>

              <Divider />

              <BlockStack gap="300">
                <Text variant="headingSm" as="h3">
                  {t('strategy.presets', 'Strategy Presets')}
                </Text>
                <InlineStack gap="300" wrap>
                  <ButtonGroup>
                    <Button
                      variant={activePreset === 'conservative' ? 'primary' : 'secondary'}
                      onClick={() => applyPreset('conservative')}
                    >
                      🛡️ {t('strategy.conservative', 'Conservative')}
                    </Button>
                    <Button
                      variant={activePreset === 'balanced' ? 'primary' : 'secondary'}
                      onClick={() => applyPreset('balanced')}
                    >
                      ⚖️ {t('strategy.balanced', 'Balanced')}
                    </Button>
                    <Button
                      variant={activePreset === 'aggressive' ? 'primary' : 'secondary'}
                      onClick={() => applyPreset('aggressive')}
                    >
                      🚀 {t('strategy.aggressive', 'Aggressive')}
                    </Button>
                  </ButtonGroup>
                  {activePreset === 'custom' && (
                    <Badge tone="info">{t('strategy.customPreset', 'Custom')}</Badge>
                  )}
                </InlineStack>

                {activePreset !== 'custom' && (
                  <Banner tone="info">
                    <p>
                      {activePreset === 'conservative' &&
                        t('strategy.conservativeDesc', 'Minimal interruptions. Best for luxury brands or audiences that prefer a calm browsing experience. Fewer popups, higher thresholds to classify intent.')}
                      {activePreset === 'balanced' &&
                        t('strategy.balancedDesc', 'A good default for most stores. Shows popups and upsells only to visitors showing real intent. Recommended for general e-commerce.')}
                      {activePreset === 'aggressive' &&
                        t('strategy.aggressiveDesc', 'Maximum engagement. Best for flash sales, discount-heavy stores, or impulse-buy products. More popups, lower thresholds, more products shown.')}
                    </p>
                  </Banner>
                )}
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Session Scoring */}
        <Layout.Section>
          <BlockStack gap="200">
            <Text variant="headingMd" as="h2">
              📊 {t('strategy.scoringTitle', 'Visitor Intent Scoring')}
            </Text>
            <Text as="p" tone="subdued">
              {t('strategy.scoringDesc', 'Assign weights to visitor actions to calculate a real-time purchase intent score. This score drives popup behavior, product counts, and upsell offers.')}
            </Text>
          </BlockStack>
        </Layout.Section>
        <Layout.Section>
          <SessionScoringForm
            settings={sessionScoring}
            onChange={handleScoringChange}
          />
        </Layout.Section>

        {/* Popup & Intent Strategy */}
        <Layout.Section>
          <BlockStack gap="200">
            <Text variant="headingMd" as="h2">
              🎯 {t('strategy.popupTitle', 'Engagement & Popups')}
            </Text>
            <Text as="p" tone="subdued">
              {t('strategy.popupDesc', 'Control exit-intent popups, upsell offers, aggressiveness per intent tier, and how many products each visitor segment sees.')}
            </Text>
          </BlockStack>
        </Layout.Section>
        <Layout.Section>
          <PopupSettingsForm
            settings={popupSettings}
            onChange={handlePopupChange}
          />
        </Layout.Section>

        {/* Help / Explanation */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h2">
                💡 {t('strategy.howItWorks', 'How It Works')}
              </Text>
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd">
                  <strong>1.</strong> {t('strategy.step1', 'As visitors browse your store, their actions (page views, time on site, add to cart, etc.) are tracked in real-time and converted into an intent score.')}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>2.</strong> {t('strategy.step2', 'Based on thresholds, each visitor is classified as Cold, Low, Medium, or High intent.')}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>3.</strong> {t('strategy.step3', 'The aggressiveness settings control how many popups, upsells, and recommendations each segment gets.')}
                </Text>
                <Text as="p" variant="bodyMd">
                  <strong>4.</strong> {t('strategy.step4', 'High-intent visitors can receive exclusive upsell offers to increase average order value.')}
                </Text>
              </BlockStack>
              <Divider />
              <Text as="p" variant="bodySm" tone="subdued">
                {t('strategy.tip', 'Tip: Start with the Balanced preset and adjust after reviewing your Analytics page for a few days.')}
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      <Box paddingBlockStart="400" paddingBlockEnd="400">
        <PageActions
          primaryAction={{
            content: t('common.save', 'Save'),
            onAction: handleSave,
            loading: isSaving,
            disabled: !hasChanges,
          }}
          secondaryActions={[
            {
              content: t('common.discard', 'Discard'),
              onAction: handleDiscard,
              disabled: !hasChanges,
            },
          ]}
        />
      </Box>

      {toastMessage && (
        <Toast content={toastMessage} onDismiss={() => setToastMessage('')} />
      )}
    </Page>
  );
}
